/*   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
import React, { useEffect, useState } from "react";
import "react-responsive-modal/styles.css";
import { Modal, ModalBody, ModalFooter, ModalHeader, Button } from "reactstrap";
import { EventBusModule } from "@vlinder-web/event-bus-module-react";
import * as S from "./styles";
import { useTimer } from "react-timer-hook";

export const STATE_OPT_MODAL_OPEN = "StateOPTModalVW:Open";
export const STATE_OPT_MODAL_CLOSE = "StateOPTModalVW:Close";
export const STATE_OPT_MODAL_TIMER_EXPIRY = "StateOPTModalVW:Expiry";
export const STATE_OPT_MODAL_OPEN_SUCCESS = "StateOPTModalVW:OpenSuccess";
export const STATE_OPT_MODAL_CLOSE_SUCCESS = "StateOPTModalVW:CloseSuccess";
export const STATE_OPT_MODAL_ACCEPTED = "StateOPTModalVW:Accept";
export const STATE_OPT_MODAL_REJECTED = "StateOPTModalVW:Reject";

export interface IStateOPTModalProps {
    open?: boolean;
}

const MODAL_ITEMS = {
    open: false,
    currentID: null,
    title: null,
    image: null,
    width: null,
    height: null,
    description: null,
    expiry: null,
};

export const StateOPTModalVW = (props: IStateOPTModalProps) => {
    const eb = EventBusModule.getInstance().getEventBus();
    const [item, setItem] = useState(MODAL_ITEMS);
    const time = new Date();
    time.setSeconds(time.getSeconds() + 60);
    const onTimerExpiry = () => {
        eb.emit(STATE_OPT_MODAL_TIMER_EXPIRY, null, {});
        onModalClose();
    };
    const { seconds, minutes, hours, days, restart, isRunning, pause } =
        useTimer({
            expiryTimestamp: time,
            autoStart: false,
            onExpire: onTimerExpiry,
        });
    // debugger;

    useEffect(() => {
        _subscribe();
        return _unsubscribe;
    });

    const _subscribe = () => {
        eb.on(STATE_OPT_MODAL_OPEN, onModalOpen);
        eb.on(STATE_OPT_MODAL_CLOSE, onModalClose);
    };

    const _unsubscribe = () => {
        eb.off(STATE_OPT_MODAL_OPEN, onModalOpen);
        eb.off(STATE_OPT_MODAL_CLOSE, onModalClose);
    };

    const onModalOpen = (props: any) => {
        if (item.open === false) {
            if (props.expiry) {
                const time = new Date();
                time.setSeconds(time.getSeconds() + props.expiry);
                restart(time);
                eb.emit(STATE_OPT_MODAL_OPEN_SUCCESS, null, {});
            }
            setItem({
                open: true,
                ...{ ...(props || {}) },
            });
        }
    };

    const onModalClose = () => {
        if (item.open) {
            if (isRunning) {
                pause();
            }
            setItem({
                ...MODAL_ITEMS,
                open: false,
            });

            eb.emit(STATE_OPT_MODAL_CLOSE_SUCCESS, null, {});
            eb.emit(STATE_OPT_MODAL_REJECTED, null, {});
        }
    };

    const onModalAccept = () => {
        if (item.open) {
            if (isRunning) {
                pause();
            }
            setItem({
                ...MODAL_ITEMS,
                open: false,
            });
            eb.emit(STATE_OPT_MODAL_ACCEPTED, null, {});
            eb.emit(STATE_OPT_MODAL_CLOSE_SUCCESS, null, {});
        }
    };

    const onModalReject = () => {
        if (item.open) {
            if (isRunning) {
                pause();
            }
            setItem({
                ...MODAL_ITEMS,
                open: false,
            });
            eb.emit(STATE_OPT_MODAL_REJECTED, null, {});
            eb.emit(STATE_OPT_MODAL_CLOSE_SUCCESS, null, {});
        }
    };

    const _renderTitle = () => {
        if (item.title) {
            return <S.Title>{item.title}</S.Title>;
        }
    };

    const _renderImage = () => {
        if (item.image) {
            return (
                <S.Image
                    src={item.image}
                    width={item.width ?? "30%"}
                    height={item.height ?? "auto"}
                />
            );
        }
    };

    const _renderDescription = () => {
        if (item.description) {
            return (
                <S.Description>
                    {item.description}
                </S.Description>
            );
        }
    };

    const _renderBody = () => {
        return (
            <S.Container>
                {_renderTitle()}
                {_renderImage()}
                {_renderDescription()}
                {/* {_renderTimer()} */}
            </S.Container>
        );
    };

    const _renderButtons = () => {
        return (
            <>
                <Button onClick={() => onModalReject()}>Skip</Button>{" "} 
                <Button color="primary" onClick={() => onModalAccept()}>
                    Login
                </Button>               
            </>
        );
    };

    const _getModal = () => {
        if (item?.open) {
            return (
                <Modal
                    isOpen={item?.open}
                    toggle={onModalClose}
                    fullwidth={true}
                    size={"lg"}
                    centered={true}
                    animation={false}
                    fade={false}
                    style={{ width: "100%" }}
                >
                    <ModalHeader toggle={onModalClose}></ModalHeader>
                    <ModalBody>{_renderBody()}</ModalBody>
                    <ModalFooter>{_renderButtons()}</ModalFooter>
                </Modal>
            );
        }
    };

    return <>{_getModal()} </>;
};
